import React, { useContext, useMemo, useRef, useState } from 'react';
import type { PaymentMethodFormProps } from './PaymentMethodForms';
import type { AetherPaymentRequest } from '../../../models/Payment';
import type {
  AdminPaymentSettings
} from '../../../models/PaymentMethodSettings';

import { AuthContext } from '../../../common/Auth';
import {
  ConfirmationTokenCreateParams,
  loadStripe,
  type StripeElementsOptions,
} from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Alert, Box, Button, Skeleton, Stack } from '@mui/material';
import { _useToasts } from '../../../common/_useToasts';
import { useFormContext } from 'react-hook-form';

export default function StripePayment({
  settings,
  index,
  methodBalanceDue,
}: PaymentMethodFormProps) {
  const { paymentMethodsSettings, settingData } = useContext(AuthContext);

  const currencyData = useMemo(() => {
    return (settingData as any[])?.find((i) => i.page === 'General');
  }, [settingData]);

  const stripeAdminSettings = useMemo(() => {
    return (paymentMethodsSettings as AdminPaymentSettings[])?.find(
      (setting) => setting.settingsName === 'stripeSettings',
    );
  }, [paymentMethodsSettings]);

  const stripeSettings = stripeAdminSettings?.values[0] 

  const stripePublicKey = stripeSettings?.publicKey;
  const currencyCode = currencyData?.values?.storeDefaultCurrency?.code;

  const elementsOptions: StripeElementsOptions = {
    mode: 'payment',
    amount: Math.round(methodBalanceDue * 100), // Stripe requires the amount in cents with no decimals
    currency: currencyCode?.toLowerCase(),
    paymentMethodCreation: 'manual',
    payment_method_types: ['card'],
  };

  if (!stripePublicKey?.length) {
    return (
      <Alert severity="error">
        Stripe has not been configured correctly , please contact support
      </Alert>
    );
  }

  const stripePromise = loadStripe(stripePublicKey);

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <StripeForm />
    </Elements>
  );
}

function StripeForm() {
  const stripe = useStripe();
  const elements = useElements();
  const submitRef = useRef<HTMLButtonElement | null>(null);
  const { _addToast } = _useToasts();
  const { getValues, setValue } = useFormContext<AetherPaymentRequest>();
  const formValues = getValues();

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  if (!stripe || !elements) {
    return (
      <Stack direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Skeleton height={200} width={'100%'} />
        <Skeleton variant="rounded" height={45} width={110} />
      </Stack>
    );
  }

  function handlePaymentRequestSubmit() {
    submitRef?.current?.click();
  }

  async function handleStripeSubmit() {
    if (!stripe || !elements) return;
    setIsProcessing(true);

    try {
      const { error: submitError } = await elements.submit();

      if (submitError) {
        _addToast(submitError.message, { appearance: 'error' });
        return;
      }


      const paymentParams: ConfirmationTokenCreateParams = {
        payment_method_data: {
          billing_details: {
            email: formValues.contact?.email?.trim(),
            name: `${formValues.contact?.firstName?.trim() ?? ''} ${formValues.contact?.lastName?.trim() ?? ''}`.trim(),
            address: {
              line1: formValues.address?.street1?.trim() ?? undefined,
              line2: formValues.address?.street2?.trim() ?? undefined,
              city: formValues.address?.city?.trim() ?? undefined,
              state: formValues.address?.state?.trim() ?? undefined,
              country: formValues.address?.country?.trim() ?? undefined,
              postal_code: formValues.address?.postalCode?.trim() ?? undefined,
            },
          },
        },
      };

      const { error, confirmationToken } = await stripe.createConfirmationToken({
        elements,
        params: paymentParams,
      });

      if (error) {
        _addToast(error.message, { appearance: 'error' });
        return;
      }

      setValue('stripeConfirmationTokenId', confirmationToken.id);
      handlePaymentRequestSubmit();
    } catch (error) {
      _addToast('An unexpected error occurred. Please try again.', { appearance: 'error' });
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <Stack direction="column" spacing={2} alignItems="center" justifyContent="center">
      <Box width="100%">
          <PaymentElement />
      </Box>
      <Button
        type="button"
        variant="contained"
        color="primary"
        size="large"
        sx={{
          width: {
            xs: '100%',
            sm: 'fit-content',
          },
          margin: '0 auto',
        }}
        loading={isProcessing}
        onClick={handleStripeSubmit}
      >
        Continue
      </Button>
      <Button type="submit" sx={{ display: 'none' }} ref={submitRef}>
        Submit
      </Button>
    </Stack>
  );
}
